/**
 * Named errors for the desktop capture failures the frontend raises itself.
 *
 * The session-flow classifier buckets a failure by `error.name` (or, for a
 * Tauri rejection, `errorCode`). The desktop recording paths used to abort with
 * a bare `new Error('...')` or an `alert()` and a `return`, both of which reach
 * the metric as an unclassified `_OTHER` — or as nothing at all. Raising one of
 * these instead is what gives a failed desktop pause or resume a semantic
 * `error.type`.
 */
export const DesktopCaptureFailure = {
  /** No input and/or output device selected, even after enumerating. */
  NO_AUDIO_DEVICE: 'DesktopNoAudioDeviceError',
  /** The selected microphone is no longer present. */
  INPUT_DEVICE_DISCONNECTED: 'DesktopInputDeviceDisconnectedError',
  /** The selected (or default) output device is no longer present. */
  OUTPUT_DEVICE_UNAVAILABLE: 'DesktopOutputDeviceUnavailableError',
  /** macOS/Windows denied the microphone at the OS level. */
  MIC_PERMISSION_DENIED: 'DesktopMicPermissionDeniedError',
  /** macOS denied screen recording, which system audio capture needs. */
  SCREEN_CAPTURE_PERMISSION_DENIED: 'DesktopScreenCapturePermissionDeniedError',
  /** The session the click named is not in the cache. */
  SESSION_NOT_FOUND: 'DesktopSessionNotFoundError',
  /** The recorder would not stop; capture may still be live. */
  RECORDER_STOP_FAILED: 'DesktopRecorderStopFailedError',
  /**
   * Heidi Remote was disconnected, so the stop did not command the device. It
   * keeps recording on its own for bulk-sync recovery.
   */
  REMOTE_DEVICE_DISCONNECTED: 'DesktopRemoteDeviceDisconnectedError',
} as const;

export type DesktopCaptureFailureName =
  (typeof DesktopCaptureFailure)[keyof typeof DesktopCaptureFailure];

export class DesktopCaptureError extends Error {
  constructor(name: DesktopCaptureFailureName, message: string) {
    super(message);
    this.name = name;
  }
}
